/**
 * Pure helpers for the shared JobFamiliesList (ER-11386, Organization > Job Families).
 *
 * Kept framework-free so unit tests can exercise status mapping, count
 * normalisation and code formatting without mounting the SFC (mirrors the
 * approach used by the sibling JobCategoriesList row helpers and existing
 * shared table rows, e.g. PayPlansTable).
 */

export const QUICK_FILTERS = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "archived", label: "Archived" },
];

const KNOWN_STATUSES = new Set(["active", "draft", "retired", "archived"]);

export function normalizeStatus(status) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  return KNOWN_STATUSES.has(normalized) ? normalized : "active";
}

const GREYED_STATUSES = new Set(["retired", "archived"]);

export function isDisabledRow(status, disabled) {
  return Boolean(disabled) || GREYED_STATUSES.has(normalizeStatus(status));
}

export function displayCode(code) {
  const raw = String(code || "").trim();
  if (!raw) return "";
  return raw.startsWith("#") ? raw : `#${raw}`;
}

function normalizeCount(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? Math.round(numeric) : 0;
}

export function normalizeLevelCount(levelCount) {
  return normalizeCount(levelCount);
}

export function normalizePositionCount(positionCount) {
  return normalizeCount(positionCount);
}

/**
 * Map a Job Family API row (ER-11112 / ER-11516) onto the list's presentational
 * shape (`levelCount` / `positionCount` camelCase). Accepts either the raw
 * serializer payload or an already-mapped list row.
 */
export function fromApiJobFamily(apiFamily) {
  const raw = apiFamily && typeof apiFamily === "object" ? apiFamily : {};
  return {
    id: raw.id ?? null,
    name: String(raw.name || "").trim(),
    code: raw.code || raw.code_str || "",
    status: raw.status,
    levelCount: raw.level_count ?? raw.levelCount,
    positionCount: raw.position_count ?? raw.positionCount,
    unread: raw.unread,
  };
}

export function buildRowView(jobFamily) {
  const raw = fromApiJobFamily(jobFamily);
  const status = normalizeStatus(raw.status);
  const disabled = isDisabledRow(status, jobFamily && jobFamily.disabled);
  const name = String(raw.name || "").trim();

  return {
    id: raw.id ?? null,
    name,
    code: displayCode(raw.code),
    status,
    disabled,
    levelCount: normalizeLevelCount(raw.levelCount),
    positionCount: normalizePositionCount(raw.positionCount),
    unread: Number(raw.unread) > 0 ? Number(raw.unread) : 0,
    // Props consumed by the shared presentation components:
    cardState: disabled ? "Disabled" : "Active",
    badgeStatus: status,
    // Retired rows render the neutral grey pill; archived uses the
    // OrgObjectStatusBadge "Archived" badge, so it is not forced neutral.
    badgeNeutral: status === "retired",
  };
}

export function nextQuickStatus(currentStatus, targetStatus) {
  return currentStatus === targetStatus ? "" : targetStatus;
}

const ACTION_META = {
  edit: { name: "Rename", icon: "icons/dropdown/edit.svg" },
  retire: { name: "Retire", icon: "icons/dropdown/activate.svg" },
  activate: { name: "Activate", icon: "icons/dropdown/activate.svg" },
  reactivate: { name: "Reactivate", icon: "icons/dropdown/activate.svg" },
  archive: { name: "Archive", icon: "icons/dropdown/archive-new.svg" },
  unarchive: { name: "Unarchive", icon: "icons/dropdown/unarchive.svg" },
  delete: {
    name: "Delete",
    icon: "icons/dropdown/trash.svg",
    destructive: true,
    negative: true,
  },
};

function menuItem(value) {
  return { value, ...ACTION_META[value] };
}

/**
 * MenuIcon items for a list/detail overflow row (same rules as Job Categories).
 * Draft → Activate + Delete-if-unassigned; Active → Retire; Retired →
 * Reactivate + Archive; Archived → Unarchive. Rename is always first.
 * Delete is draft-only and only when no positions are assigned (backend rule).
 */
export function rowActionsFor(row) {
  const view = row || {};
  const status = normalizeStatus(view.status);
  const positionCount = normalizePositionCount(view.positionCount);
  const actions = [menuItem("edit")];

  if (status === "archived") {
    actions.push(menuItem("unarchive"));
    return actions;
  }

  if (status === "draft") {
    actions.push(menuItem("activate"));
    if (positionCount === 0) actions.push(menuItem("delete"));
    return actions;
  }

  if (status === "active") {
    actions.push(menuItem("retire"));
    return actions;
  }

  if (status === "retired") {
    actions.push(menuItem("reactivate"));
    actions.push(menuItem("archive"));
    return actions;
  }

  return actions;
}
